// ©2024 Austin App House. All rights reserved.
import React, { useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  Field, Form, reduxForm, reset, initialize, getFormValues,
} from 'redux-form';
import { startDateFilter } from '../../utils/pickerDateFilters';
import { parseFormDate } from '../../utils/getDateWithOffset';
import DatePickedField from '../layout/reduxForm/customFields/DatePickerField';
import TimePickedField from '../layout/reduxForm/customFields/TimePickerField';
import InputField from '../layout/reduxForm/customFields/InputField';
import SelectField from '../layout/reduxForm/customFields/SelectField';

const getApplyFormInitialValues = (workOrder = {}) => ({
  startDate: parseFormDate(workOrder.startDate),
  startTime: workOrder.startTime || '',
  basePayBidAmount: workOrder.amount != null ? workOrder.amount : '',
  basicType: workOrder.basicType || '',
  variablePayBidAmount: workOrder.variableAmount != null ? workOrder.variableAmount : '',
});

let FormApplyModal = ({
  currentWorkOrder,
  modalState,
  handleSubmit,
  toggleModal,
  submitApply,
}) => {
  const dispatch = useDispatch();
  const formValues = useSelector(state => getFormValues('FormApplyModel')(state));

  const [isCounterOffer, setIsCounterOffer] = useState(modalState?.type?.openCounterOffer || false);

  const openCounterOffer = () => {
    dispatch(initialize('FormApplyModel', {
      ...getApplyFormInitialValues(currentWorkOrder),
      comment: formValues?.comment || '',
    }));
    setIsCounterOffer(true);
  };

  const rateTypes = currentWorkOrder.basicType === 'Device' ? [{
    value: 'Device',
    label: 'Device',
  }] : [
    {
      value: 'Hour',
      label: 'Hour',
    },
    {
      value: 'Site',
      label: 'Site / Flat',
    },
  ];

  const submit = (formData) => {
    submitApply(currentWorkOrder, formData, toggleModal, isCounterOffer, true);
  };

  return (
    <div className="content form-apply-modal">

      <Form onSubmit={handleSubmit(submit)}>
        {isCounterOffer ? (
          <>
            <div className="field_row">
              <div className="field_name">
                <label htmlFor="apply-start-date">
                  Start Date
                  <span className="requiredField">*</span>
                </label>
              </div>
              <div className="field_block">
                <Field
                  name="startDate"
                  component={DatePickedField}
                  inputClassName="form-control width-inherit dashboard-input"
                  dateFormat="MM/dd/yyyy"
                  minDate={new Date()}
                  filterDate={startDateFilter}
                  required
                />
              </div>
            </div>

            <div className="field_row">
              <div className="field_name">
                <label htmlFor="apply-start-time">
                  Start Time
                  <span className="requiredField">*</span>
                </label>
              </div>
              <div className="field_block">
                <Field
                  name="startTime"
                  component={TimePickedField}
                  inputClassName="apply-time-input"
                  timePickerClassName="apply-time-picker"
                  required
                />
              </div>
            </div>

            <BasePayBidAmount rateTypes={rateTypes} />

            <VariablePayBidAmount currentWorkOrder={currentWorkOrder} />

            <AddTravelExpense />
          </>
        ) : null}

        <div className="field_row">
          <div className="field_name">
            <label htmlFor="Comment">Comments</label>
          </div>
          <div className="field_block">
            <Field
              name="comment"
              component={Comment}
            />
          </div>
        </div>

        <div className="dashboard-modal-footer">
          <button
            className="dashboard-btn dashboard-btn--primary"
            type="submit"
          >
            Submit
          </button>

          {!isCounterOffer ? (
            <button
              color="secondary"
              className="standard_btn orange_btn"
              type="button"
              onClick={openCounterOffer}
            >
              Counter Offer
            </button>
          ) : null}

          <button
            className="dashboard-btn dashboard-btn--secondary"
            type="button"
            onClick={() => {
              dispatch(reset('FormApplyModel'));
              toggleModal(!modalState.isOpen, 'applyModal');
            }}
          >
            Cancel
          </button>
        </div>

      </Form>

    </div>
  );
};

const VariablePayBidAmount = ({ currentWorkOrder }) => (
  (currentWorkOrder.variableAmount)
    && (currentWorkOrder.variableAmount !== 0)
    && (currentWorkOrder.variableAmount !== '0') ? (
      <div className="field_row">
        <div className="field_name">
          <label>Variable Pay Bid Amount</label>
        </div>
        <div className="field_block form-apply-modal__variable-pay">
          <Field
            name="variablePayBidAmount"
            component={InputField}
            type="number"
            placeholder="$0.00"
            paddingBottomClassName=""
            inputClassName="dashboard-input"
            min={0.00}
          />
          {(currentWorkOrder.variableType && currentWorkOrder.variableType !== '') ? (
            <p className="form-apply-modal__variable-type">
              {`Per ${(currentWorkOrder.variableType === 'Hours')
                ? 'Hour'
                : currentWorkOrder.variableType
              }`}
            </p>
          ) : null}
        </div>
      </div>
    ) : null
);

const BasePayBidAmount = ({ rateTypes }) => (
  <>
    <div className="field_row">
      <div className="field_name">
        <label>Base Pay Bid Amount</label>
      </div>
      <div className="field_block">
        <Field
          name="basePayBidAmount"
          component={InputField}
          type="number"
          placeholder="$0.00"
          paddingBottomClassName=""
          inputClassName="dashboard-input"
          min={0.00}
        />
      </div>
    </div>
    <div className="field_row">
      <div className="field_name">
        <label>Per</label>
      </div>
      <div className="field_block">
        <Field
          name="basicType"
          component={SelectField}
          inputClassName="browser-default custom-select per dashboard-input"
          options={rateTypes}
        />
      </div>
    </div>
  </>
);

const AddTravelExpense = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="form-apply-modal__travel">
      <button
        type="button"
        color="orange"
        className="standard_btn orange_btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        $ Add Travel expenses
      </button>
      {isOpen ? (
        <>
          <Amount />
          <Description />
        </>
      ) : null}
    </div>
  );
};

const Amount = () => (
  <div className="field_row">
    <div className="field_name">
      <label>Amount</label>
    </div>
    <div className="field_block">
      <Field
        name="amountExpense"
        component={InputField}
        type="number"
        placeholder="$0.00"
        paddingBottomClassName=""
        inputClassName="form-control small-styled-input dashboard-input"
        min={0.00}
      />
    </div>
  </div>
);

const Description = () => (
  <div className="field_row">
    <div className="field_name">
      <label htmlFor="Description">Travel Expense Description</label>
    </div>
    <div className="field_block">
      <Field
        name="descriptionExpense"
        component={TravelDescription}
        type="text"
      />
    </div>
  </div>
);

const TravelDescription = ({ input }) => (
  <textarea
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...input}
    className="form-control commenttextarea dashboard-input"
    id="Description"
    rows="2"
    name="Description"
  />
);

const Comment = ({ input }) => (
  <textarea
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...input}
    className="form-control commenttextarea dashboard-input"
    id="Comment"
    rows="2"
  />
);

FormApplyModal = reduxForm({
  form: 'FormApplyModel',
})(FormApplyModal);

export default connect(state => ({
  initialValues: getApplyFormInitialValues(state.workOrder?.currentWorkOrder),
}))(FormApplyModal);
